import React, { FC } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Box, Text, ButtonAppearance, Spinner, theme } from '@bechtel/gds';
import { DrawerFooterButtonStyled } from '@bechtel/client-common';
import { DELETE_ROLE, GET_ROLES } from '@bechtel/client-gql';
import { AuthRole, DeleteRoleError, DeleteRolePayload, Scalars } from '@bechtel/types';
import { filter, get } from 'lodash';
import { ApolloError, useMutation } from '@apollo/client';
import { t, Trans } from '@lingui/macro';

interface DeleteRoleProps {
  role: AuthRole;
  onCancel: () => void;
}

const getErrorMessage = (error: DeleteRoleError | ApolloError) => {
  if (error instanceof ApolloError)
    return (
      <Trans>
        Network error has occured. Please cancel the form and try delete again after refreshing the roles list.
      </Trans>
    );
  switch (error) {
    case DeleteRoleError.NOT_FOUND:
      return <Trans>Role not found. Please cancel the form and refresh the roles list.</Trans>;
    case DeleteRoleError.ROLE_IS_ASSIGNED:
      return <Trans>Role cannot be deleted as some user(s) are assigned to this role.</Trans>;
    case DeleteRoleError.MISSING_REQUIRED_PERMISSION:
      return (
        <Trans>
          No permission to delete the role. Please contact system administrator for delete role permissions.
        </Trans>
      );
    case DeleteRoleError.DEFAULT_ROLE_CAN_NOT_BE_DELETED:
      return <Trans>Default role cannot be deleted.</Trans>;
    default:
      return (
        <Trans>
          An error has occured. Please cancel the form and try delete again after refreshing the roles list.
        </Trans>
      );
  }
};

export const DeleteRole: FC<DeleteRoleProps> = ({ role, onCancel }) => {
  const [deleteRole, { data, loading, error }] = useMutation<{ deleteRole: DeleteRolePayload }, { id: Scalars['ID'] }>(
    DELETE_ROLE,
    {
      //@ts-ignore deleteRole will be there on data
      update: (cache, { data: { deleteRole } }) => {
        if (!deleteRole || !deleteRole.id || error) return;
        //@ts-ignore roles will be there from cache.readQuery
        const { roles } = cache.readQuery({ query: GET_ROLES });
        cache.writeQuery({
          query: GET_ROLES,
          data: {
            roles: filter(roles, (role) => role.id != deleteRole.id),
          },
        });
      },
    }
  );
  const deleteRoleError = get(data, 'deleteRole.error') || error;

  return (
    <Modal visible={true} style={{ width: '552px', height: '430px' }}>
      <ModalHeader title={t`Delete Role`}></ModalHeader>
      <ModalBody>
        <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
          <Box
            px={{ sm: '500' }}
            py={{ sm: '600' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            alignItems={{ sm: 'center' }}
            flexGrow={{ sm: '1' }}
          >
            {!role.numUsers || role.numUsers == 0 ? (
              <>
                <Text size="500" family="graphik" color="grey.1000" weight="600" style={{ lineHeight: 1.5 }}>
                  <Trans>Are you sure you want to delete the </Trans>
                  {role.name}
                  <Trans> role?</Trans>
                </Text>
                <Text size="400" family="graphik" color="grey.1000" style={{ marginTop: `${theme.spacings[500]}` }}>
                  <Trans>This action cannot be undone.</Trans>
                </Text>
              </>
            ) : (
              <>
                <Text size="500" family="graphik" color="grey.1000" weight="600">
                  <Trans>Sorry, you can&apos;t delete the</Trans> {role.name} <Trans>role.</Trans>
                </Text>
                <Text size="400" family="graphik" color="grey.1000" style={{ marginTop: `${theme.spacings[500]}` }}>
                  {role.numUsers > 1
                    ? role.numUsers + t` users are assigned to this role on your project.`
                    : role.numUsers + t` user assigned to this role on your project.`}
                </Text>
                <Text size="400" family="graphik" color="grey.1000" style={{ marginTop: `${theme.spacings[400]}` }}>
                  <Trans>You can only delete roles with no user(s) assigned.</Trans>
                </Text>
              </>
            )}
          </Box>
          {deleteRoleError && (
            <Box justifyContent={{ sm: 'flex-end' }} pb={{ sm: '500' }} px={{ sm: '500' }}>
              <Text size="300" color="bechtel.red">
                {getErrorMessage(deleteRoleError)}
              </Text>
            </Box>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          {!role.numUsers || role.numUsers == 0 ? (
            <>
              <Box mr={{ sm: '400' }}>
                <DrawerFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onCancel}>
                  <Box px={{ sm: '500' }}>
                    <Trans>Cancel</Trans>
                  </Box>
                </DrawerFooterButtonStyled>
              </Box>
              <Box>
                <DrawerFooterButtonStyled
                  appearance={ButtonAppearance.SECONDARY}
                  onClick={async () => {
                    await deleteRole({
                      variables: {
                        id: role.id,
                      },
                    });
                    if (!deleteRoleError) onCancel();
                  }}
                >
                  <Box px={{ sm: '500' }}>
                    {loading && (
                      <>
                        <Spinner></Spinner>
                        &nbsp;&nbsp;
                      </>
                    )}
                    <Trans>Delete</Trans>
                  </Box>
                </DrawerFooterButtonStyled>
              </Box>
            </>
          ) : (
            <Box>
              <DrawerFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={onCancel}>
                <Box px={{ sm: '500' }}>
                  <Trans>Okay</Trans>
                </Box>
              </DrawerFooterButtonStyled>
            </Box>
          )}
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteRole;
